<template>
  <section>
    <div class="tw-mb-4 tw-flex tw-items-center tw-gap-2">
      <MessageSquare class="tw-h-4 tw-w-4 tw-text-secondary-500 dark:tw-text-neutral-400" />
      <h2
        class="tw-font-heading tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary-700 dark:tw-text-neutral-300"
      >
        {{ t('orders.titles.comments') }}
      </h2>
    </div>

    <!-- Comments List -->
    <div ref="commentsContainer" class="tw-space-y-2.5 tw-max-h-[450px] tw-overflow-y-auto tw-pr-1">
      <template v-if="isLoading">
        <div
          v-for="i in 3"
          :key="i"
          class="tw-rounded-xl tw-border tw-border-surface-200 tw-p-3.5 dark:tw-border-neutral-800"
        >
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Skeleton class="tw-h-4 tw-w-24" />
            <Skeleton class="tw-h-3 tw-w-16" />
          </div>
          <Skeleton class="tw-h-10 tw-w-full tw-rounded-lg" />
        </div>
      </template>

      <!-- Comments -->
      <template v-else-if="comments.length">
        <article
          v-for="comment in comments"
          :key="comment.id"
          class="tw-rounded-xl tw-border tw-border-surface-200 tw-bg-surface-50/70 tw-p-3.5 dark:tw-border-neutral-800 dark:tw-bg-neutral-900/40"
        >
          <div class="tw-mb-1.5 tw-flex tw-items-center tw-gap-2">
            <Avatar size="sm" shape="circle">
              <AvatarImage
                v-if="comment.author?.profile_picture"
                :src="comment.author.profile_picture"
                :alt="comment.author?.display_name"
              />
              <AvatarFallback
                class="tw-bg-secondary-100 tw-text-[0.65rem] tw-font-semibold tw-text-secondary-700 dark:tw-bg-neutral-800 dark:tw-text-neutral-200"
              >
                {{ getInitials(comment.author?.display_name) }}
              </AvatarFallback>
            </Avatar>
            <span
              class="tw-text-sm tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100"
            >
              {{ comment.author?.display_name }}
            </span>
            <span class="tw-ml-auto tw-text-xs tw-text-surface-500 dark:tw-text-neutral-400">
              {{ moment(comment.created_on).format('DD.MM.YYYY') }}
            </span>
          </div>
          <p
            class="tw-pl-9 tw-text-sm tw-text-secondary-700 dark:tw-text-neutral-300 tw-whitespace-pre-wrap tw-leading-relaxed"
          >
            {{ comment.text }}
          </p>
        </article>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-surface-200 tw-py-12 dark:tw-border-neutral-800"
      >
        <MessageSquare class="tw-h-11 tw-w-11 tw-text-surface-300 dark:tw-text-neutral-600" />
        <p class="tw-mt-3 tw-text-sm tw-font-medium tw-text-surface-500 dark:tw-text-neutral-400">
          {{ t('requests.titles.no_comments') }}
        </p>
      </div>
    </div>

    <!-- Comment Input -->
    <div
      class="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-surface-200 tw-bg-surface-50 tw-p-2 dark:tw-border-neutral-800 dark:tw-bg-neutral-900/50"
      :title="
        isDisabled
          ? t('requests.titles.comments_allowed_in_pending_and_request_clarification_status')
          : ''
      "
    >
      <Input
        v-model="newComment"
        :placeholder="t('orders.titles.message_hint')"
        :disabled="isDisabled"
        @keypress.enter="addComment"
        class="tw-flex-1 tw-border-0 tw-bg-transparent tw-shadow-none focus-visible:tw-ring-0"
        :class="{ 'tw-cursor-not-allowed': isDisabled }"
      />
      <Button @click="addComment" :disabled="isDisabled || !newComment.trim()" size="sm">
        <Send class="tw-h-4 tw-w-4" />
        {{ t('common.forms.send') }}
      </Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { usePaymentStore } from '@/stores/payment.store';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Send } from 'lucide-vue-next';

const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { getPaymentComments, createPaymentComments } = usePaymentStore();

const props = defineProps<{
  payment: any;
  paymentId: number | null;
}>();

const comments = ref<any[]>([]);
const newComment = ref('');
const isLoading = ref(true);
const commentsContainer = ref<HTMLElement | null>(null);

const isDisabled = computed(() => {
  return props.payment?.status === 'DRAFT';
});

const loadComments = async () => {
  if (!props.paymentId) return;
  isLoading.value = true;
  const response = await getPaymentComments(props.paymentId);
  comments.value = response.results || [];
  isLoading.value = false;
  await nextTick();
  scrollToBottom();
};

const addComment = async () => {
  if (!newComment.value.trim() || !props.paymentId) return;

  await handleApiError(async () => {
    await createPaymentComments({
      payment: props.paymentId,
      text: newComment.value,
    });
    await loadComments();
    newComment.value = '';
  }, t('requests.titles.successful_comment_added'));
};

const scrollToBottom = () => {
  if (commentsContainer.value) {
    commentsContainer.value.scrollTop = commentsContainer.value.scrollHeight;
  }
};

onMounted(loadComments);

watch(() => props.paymentId, loadComments);
</script>
